import React, { createContext, ReactElement, useContext, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import useAuthApi from "../../api/Auth/AuthAPI";
import { useLogger } from "./LoggerProvider";
import { UserProviderContext } from "./UserProvider";
import { useCookies } from "react-cookie";
import { logoutEdgeAsync } from "tendril-v2/src/Data/DataSource/edge/auth/logoutEdgeAsync";
import { type ApiResponse } from "../../types/ApiResponse";
import { type AuthenticationResponse } from "tendril-v2/src/Data/DataSource/auth/login/dto/login.dto";
import { PublicRoutes } from "../../constants/routesConnect";
import { EventEmitter } from "events";
import {showUIToast} from "../../core/ui/UIToast";
import { ERROR_CODES } from "../shared/enums/errorAuthCodes";

// Global object for browser-like environment
const window = globalThis as any;

// EventEmitter instance to allow centralization of session-related event handling
export const sessionEventEmitter = new EventEmitter();

interface AuthContextProps {
  startSession: (credentials: { email: string; password: string }) => void;
  endSession: () => void;
}

const AuthProviderContext = createContext<AuthContextProps | undefined>(
  undefined
);

interface Props {
  children: ReactElement;
}

export const AuthProvider = ({ children }: Props): JSX.Element => {
  // Generate unique ID for each tab
  const [tabId] = useState<string>(uuidv4());

  // Store the timestamp to detect session expiration or multiple tabs
  const [timestamp, setTimestamp] = useState<number>(() =>
    new Date().getTime()
  );

  // Session identifier state to store token and tab UUID
  const [sessionIdentifiers, setSessionIdentifiers] = useState<{
    authToken: string | null;
    tabUuid: string | null;
  }>({ authToken: null, tabUuid: null });

  // Use cookies to manage auth-related data in the browser
  const [cookie, setCookie] = useCookies([
    "tabUuid",
    "access_token",
    "refresh_token",
  ]);

  // Reference to track last refresh time (prevents frequent refresh requests)
  const lastRefreshTimeRef = useRef<number>(0);
  // Authentication API (login, logout, refresh, and user info endpoints)
  const { login, logout, getMe, refresh } = useAuthApi();
  // Current user information stored in the user provider context
  const { currentUser } = useContext(UserProviderContext);
  // Logger for debugging and event tracking
  const { logger } = useLogger();
  const loggerRef = useRef(logger);

  // State to show a loading or redirecting UI during session refresh
  const [isSessionRefreshing, setIsSessionRefreshing] = useState(false);

  // Error state for failed redirection
  const [errorRedirect, setErrorRedirect] = useState(false);

  /** BroadcastChannel to detect duplicate tabs and enforce single active session per browser */
  const authChannel = new BroadcastChannel("auth_channel");

  authChannel.onmessage = (event) => {
    handleAuthChannelMessage(
      event,
      timestamp,
      tabId,
      setIsSessionRefreshing,
      safeRedirect,
      logger,
      currentUser,
      PublicRoutes
    );
  };

  /**
   * Handles a message event received through the authChannel to detect duplicate tabs.
   *
   * This method checks if another tab with the same session is open by comparing timestamps and tab IDs.
   * If a duplicate tab is detected, it triggers a session refresh, logs the situation,
   * and safely redirects the user to the login page to handle the duplicate session.
   *
   * @param event - The message event received from the authChannel containing tab information.
   * @param timestamp - The current tab's timestamp for comparison.
   * @param tabId - The unique identifier of the current tab.
   * @param setIsSessionRefreshing - Function to set the session refreshing state (e.g., to show a loading indicator).
   * @param safeRedirect - Function to handle safe redirection to a given URL.
   * @param logger - Logger utility used to log warnings and debug information.
   * @param currentUser - (Optional) Information about the current user, such as userId.
   * @param PublicRoutes - (Optional) Object containing public route paths, like the login route.
   */
  function handleAuthChannelMessage(
    event: MessageEvent,
    timestamp: number,
    tabId: string,
    setIsSessionRefreshing: (refreshing: boolean) => void,
    safeRedirect: (url: string) => void,
    logger: any,
    currentUser?: { userId?: string },
    PublicRoutes?: { LOGIN?: string }
  ): void {
    // Detects message type and compares tab timestamps
    if (
      event.data.type === "check_open_tabs" &&
      Math.floor(timestamp / 100) < Math.floor(event.data.timestamp / 100)
    ) {
      if (event.data.tabId !== tabId) {
        setIsSessionRefreshing(true); // Set loading state during redirect
        logger.warn(
          `Refreshing session, duplicated tab, userId:` + (currentUser?.userId || "unknown"),
          { componentName: "AuthProvider" }
        );

        setTimeout(() => {
          setIsSessionRefreshing(false);
          const loginUrl = PublicRoutes?.LOGIN
            ? `/${PublicRoutes.LOGIN}?${ERROR_CODES.DUPLICATED_SESSION_TAB.key}`
            : `/login?${ERROR_CODES.DUPLICATED_SESSION_TAB.key}`;
          safeRedirect(loginUrl); // Redirection for duplicate tabs
        }, 500);
      }
    }
  }


  /**
   * Handles safe navigation to prevent application crashes during URL redirection.
   * Shows fallback UI if the redirection fails.
   */
  const safeRedirect = (url: string) => {
    try {
      if (!PublicRoutes.LOGIN) {
        throw new Error(
          "PublicRoutes.LOGIN is not correctly defined! Check your routes configuration."
        );
      }
      window.location.replace(url); // Redirects to the specified URL
    } catch (error) {
      setErrorRedirect(true); // Show fallback UI
      logger.error(`Failed to redirect, userId: ${currentUser.userId}`, {
        componentName: AuthProvider.name,
      });
    }
  };

  /**
   * Starts a session by logging in the user and setting session tokens.
   */
  const startSession = async (credentials: {
    email: string;
    password: string;
  }) => {
    const { success, data }: ApiResponse<AuthenticationResponse> =
      await login(credentials);

    if (success && data) {
      setSessionIdentifiers({ authToken: data.access_token, tabUuid: null });
    }
  };

  /**
   * Ends the session by clearing session data, cookies, and notifying Edge and Auth services.
   */
  const endSession = async () => {
    try {
      await logoutEdgeAsync(window.localStorage.getItem("target"));
      window.localStorage.removeItem("target"); // Clean up target from local storage
      await logout();

      // Clear cookies and session identifiers
      setCookie("access_token", "", { path: "/" });
      setCookie("refresh_token", "", { path: "/" });
      setSessionIdentifiers({ tabUuid: null, authToken: null });

      loggerRef.current.info(
        `User session ended: [id:${currentUser.userId}, email:${currentUser.email}]`,
        {
          componentName: AuthProvider.name,
        }
      );
    } catch (error) {
      loggerRef.current.error(
        `Error ending session: [id:${currentUser.userId}, email:${currentUser.email}]`,
        {
          componentName: AuthProvider.name,
        }
      );
    }
  };

  /**
   * Refreshes the session, updates session tokens, or redirects if the session is invalid.
   */
  const refreshSession = async () => {
    const currentTime = new Date().getTime();

    // Prevents multiple refresh attempts within 5 seconds
    if (currentTime - lastRefreshTimeRef.current <= 5000) return;

    lastRefreshTimeRef.current = new Date().getTime();
    setIsSessionRefreshing(true);
    logger.warn(`Refreshing session, userId:` + currentUser.userId, {componentName: AuthProvider.name});

    try {
      const { data } = await refresh(); // Attemps to refresh the session token

      if (!data) {
        loggerRef.current.error(
          `User was dropped out due to session duplication: ${currentUser.email}`,
          {
            componentName: AuthProvider.name,
          }
        );

        await endSession();

        setTimeout(() => {
          safeRedirect(`/${PublicRoutes.LOGIN}?${ERROR_CODES.DUPLICATED_SESSION.key}`); // Redirect due to duplication
        }, 500);
      } else {
        setSessionIdentifiers({ tabUuid: null, authToken: data.access_token });
        setCookie("access_token", data.access_token);

        loggerRef.current.info(
          `Session refreshed successfully for: ${currentUser.email}`,
          {
            componentName: AuthProvider.name,
            userId: currentUser.userId,
          }
        );
        setIsSessionRefreshing(false); // Stop loading
      }
    } catch (error) {
      setIsSessionRefreshing(false);
    }
  };

  /**
   * Validates the session at regular intervals and refreshes or ends the session if needed.
   */
  const checkSessionValidity = async () => {
    const sessionCheckInterval = setInterval(async () => {
      const queryParameters = window.location.pathname;
      if (queryParameters !== "/login") {
        // Notify other tabs about current session
        authChannel.postMessage({
          type: "check_open_tabs",
          timestamp: timestamp,
          tabId,
        });

        // Verify session validity, refresh if needed
        const { success } = await getMe();
        if (!success) {
          await refreshSession().then(() => {
            return () => clearInterval(sessionCheckInterval);
          });
        }
      }
    }, 10 * 1000); // Check session every 10 seconds

    return () => clearInterval(sessionCheckInterval);
  };

  useEffect(() => {
    loggerRef.current = logger; // Update logger reference
  }, [logger, currentUser]);

  useEffect(() => {
    const handleSessionExpired = ({
                                    reason,
                                    redirectUrl,
                                  }: {
      reason: string;
      redirectUrl: string;
    }) => {
      setIsSessionRefreshing(true);
      logger.warn(`Refreshing session because session is expired,  userId:` + currentUser.userId, {componentName: AuthProvider.name});

      setTimeout(() => {
        safeRedirect(redirectUrl); // Handle session expiration centrally
      }, 500);
    };

    sessionEventEmitter.on("sessionExpired", handleSessionExpired);
    checkSessionValidity().finally();

    return () => {
      sessionEventEmitter.off("sessionExpired", handleSessionExpired);
    };
  }, []);

  const contextValue: AuthContextProps = {
    startSession,
    endSession,
  };

  return (
    <>
      {/* Loading UI while redirecting */}
      {isSessionRefreshing && (
        showUIToast({
          type: 'info',
          text: 'Redirecting to login page. Please wait...',
        })
      )}
      {/* Error UI on failed redirection */}
      {errorRedirect && (
        showUIToast({
          type: 'error',
          text: 'Something went wrong while redirecting. Please refresh the page.',
        })
      )}
      <AuthProviderContext.Provider value={contextValue}>
        {children} {/* Render children wrapped in AuthProvider */}
      </AuthProviderContext.Provider>
    </>
  );
};

/**
 * Hook to access the authentication context.
 */
export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthProviderContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
